/* CURRENCY KEEP — READING ANOTHER COLLECTOR'S PHOTOGRAPHS.
 *
 * PHASE 4, STEP 7 — the Community read path, session 2 of 2. This is the
 * client half of `functions/_lib/photo-batch.js`.
 *
 * ══ ⚠⚠⚠ THIS IS THE FIRST TIME THE FEATURE WORKS AT ALL ════════════
 *
 * It is NOT a performance change and NOT a storage cleanup. Until this
 * module, `photos.js` read `notevault:pubph:<user>:<hid>:<slot>`, which
 * `remote.js` routes to LOCAL IndexedDB — `PUB_PREFIX` is
 * `'notevault:pub:'` and `'notevault:pubph:…'` does not start with it, the
 * colon at index 13 being the difference. **A stranger calling
 * `getPubPhoto(theirName, …)` read their OWN IndexedDB under someone
 * else's username and found nothing.** Measured 2026-08-26. No stranger
 * has ever seen a Community photograph, from the day the feature shipped.
 *
 * Jesse's framing is the record: *"the next session is the first time it
 * works for someone other than the owner."*
 *
 * ══ ⚠⚠⚠ `unavailable` VS `missing`, AND WHY THE SPLIT IS NOT PER
 *    PHOTOGRAPH HERE ═══════════════════════════════════════════════════
 *
 * `photo-view.js` keeps six states apart because a vault screen can act
 * on the difference: *"not on this device"* is PERMANENT and tells the
 * collector to re-photograph the note; *"could not load"* is TRANSIENT
 * and tells them to retry. **That distinction cannot be made per
 * photograph on this screen, and the reason is not that it was skipped.**
 *
 * The batch route answers with `photos[]` and a bare `unresolved[]` id
 * list carrying **no reason at all**, deliberately — it merges *no such
 * holding* · *not yours and not public* · *public but the slot is empty* ·
 * *malformed ref*, and `fetchResolved`'s drops (`object_missing`,
 * `store_unreachable`, `bad_key`) land there too. Telling them apart is
 * an enumeration oracle and this project has already had to fix one of
 * those on the sign-in screen.
 *
 * ⚠ SO ANY PER-TILE "couldn't load" HERE WOULD BE THIS MODULE INVENTING
 * A REASON THE SERVER REFUSED TO GIVE IT. That is the guess, not the
 * measurement, and this file does not make it.
 *
 * **The split therefore moves up one level, to the REQUEST**, which is
 * the one place the client knows something true:
 *
 *   · the request succeeded and an id came back with bytes  → the photo
 *   · the request succeeded and an id did not               → nothing.
 *     The tile stays a plain denomination chip, exactly as before. It
 *     says "this collector is not showing you a photograph of this
 *     note", which is the only sentence the response supports.
 *   · the request FAILED                                    → `failed`,
 *     and the screen says so ONCE, with a Retry. Transient, actionable,
 *     and not spelled out twenty times across tiles.
 *
 * ⚠⚠ AND THE PROPERTY THAT HOLDS THAT DESIGN UP IS IN §4 OF THE GATE: A
 * FAILED REQUEST MUST NOT COME BACK AS "EVERYTHING UNRESOLVED". If a
 * throw or a 502 fell through into `unresolved`, every tile would render
 * the permanent-sounding answer for a transient fault — the
 * unavailable-vs-missing collapse arriving through the REPORT instead of
 * through the pixel. `unresolved` is populated only from a request that
 * actually answered.
 *
 * ⚠ WHAT THIS COSTS, STATED RATHER THAN GLOSSED: a photograph whose
 * bucket object has genuinely vanished looks identical to a note nobody
 * ever photographed. That collapse is the ENDPOINT'S, chosen for the
 * oracle above; the operator sees it as `photo-batch: dropped <id> —
 * object_missing` in the Function log, which is where that fault belongs.
 *
 * ══ ⚠ NO STORAGE, NO WRITES, NO DELETES ════════════════════════════
 * This module imports nothing from `storage.js`, `state.js` or `idb.js`.
 * It reads a network and fills a Map. The cache holds `data:` URLs
 * rather than blob URLs, so unlike `photo-view.js`'s `tierCache` there is
 * nothing to revoke; sign-out RELOADS the page (`views-auth.js`), which
 * clears it by construction.
 */

/**
 * ⚠⚠ THIS MUST NOT DRIFT FROM `BATCH_MAX_IDS` IN
 * `functions/_lib/photo-batch.js`. The server refuses a request carrying
 * more, with a 400 — so a client chunk one larger than the server's cap
 * turns every full profile into a hard failure, and a client chunk
 * SMALLER quietly doubles the PostgREST reads the batch route exists to
 * avoid.
 *
 * It is declared here rather than imported because that module pulls in
 * `photo-delivery.js` and belongs to the Worker, not the bundle.
 * **`community-photos-check` §1 imports BOTH and asserts they are equal**,
 * so the duplication is checked rather than trusted — the two-copies-that-
 * agree-today shape this project has been burned by.
 */
export const BATCH_CHUNK = 40

/** A slot in the cache meaning "the server answered, and it did not give
 *  us this one". ⚠ A distinct value from `undefined` (never asked) and
 *  from a data URL, so a re-render can tell the three apart without
 *  re-issuing the request. */
export const UNRESOLVED = Symbol('unresolved')

/** Why a load failed. ⚠ `SIGNED_OUT` is NOT retryable and the screen must
 *  not offer a Retry for it — the fix is signing in, and a button that
 *  cannot work is worse than no button. */
export const FAILURE = Object.freeze({
  SIGNED_OUT: 'signed-out',
  REQUEST_FAILED: 'request-failed',
  BAD_RESPONSE: 'bad-response',
})

/* ── the cache ─────────────────────────────────────────────────────── */

/**
 * ⚠ WITHOUT THIS, EVERY KEYSTROKE REFETCHES THE PROFILE. `hydratePhotos`
 * runs on every `render()`, and `vPublicProfile` carries a search box and
 * a filter sheet — so an uncached load would put a batch request behind
 * every character typed into it. Same reason `photo-view.js` caches
 * tiers, and the same shape of bug if it did not.
 *
 * ⚠⚠ THE FAILURE IS REMEMBERED TOO, AND THAT IS NOT AN OPTIMISATION. A
 * remembered failure is what stops a dead endpoint being hammered once
 * per keystroke. It is cleared by `retryCommunityPhotos` and by nothing
 * else, so the Retry button is the only thing that asks again — which is
 * what makes it a Retry rather than a decoration.
 */
export function makeCommunityPhotoCache() {
  return { entries: new Map(), failures: new Map() }
}

const _cache = makeCommunityPhotoCache()

/* ⚠ THE USERNAME IS IN THE KEY. A holding id is a uuid and cannot
 * collide across collectors, so this is not load-bearing today — it is
 * here so that cross-profile bleed is impossible BY CONSTRUCTION rather
 * than by a uniqueness property living in another module. */
const entryKey = (username, subject, slot, tier, id) =>
  `${username} ${subject} ${slot} ${tier} ${id}`
const scopeKey = (username, subject, slot, tier) =>
  `${username} ${subject} ${slot} ${tier}`

/** Drop everything. Not wired to sign-out — that reloads the page — but
 *  exported so a console helper and the gate can reset without one. */
export function clearCommunityPhotos(cache = _cache) {
  cache.entries.clear()
  cache.failures.clear()
}

/**
 * Forget the remembered failure for one profile view so the next load
 * asks again.
 *
 * ⚠ IT DOES NOT CLEAR THE `UNRESOLVED` ENTRIES, AND THAT IS THE POINT.
 * Those came from a request that SUCCEEDED and answered; re-asking would
 * be re-asking a question already answered. Only the ids inside a chunk
 * that failed are uncached, so they are the only ones a retry re-asks.
 */
export function retryCommunityPhotos({ username, subject = 'note', slot, tier }, cache = _cache) {
  return cache.failures.delete(scopeKey(username, subject, slot, tier))
}

/* ── the request ───────────────────────────────────────────────────── */

/**
 * Split ids into request-sized groups, deduped, order preserved.
 *
 * ⚠ THE DEDUPE IS NOT A NICETY. Two tiles for one holding would spend two
 * of the forty cap slots on the same photograph, and the server dedupes
 * anyway (`parseBatchIds`), so an undeduped client chunk of 40 can carry
 * fewer than 40 distinct ids and silently under-fill the batch.
 */
export function chunkIds(ids, size = BATCH_CHUNK) {
  if (!(size > 0)) throw new Error('chunkIds: size must be positive')
  const seen = new Set()
  const out = []
  for (const raw of ids ?? []) {
    const id = String(raw ?? '')
    if (!id || seen.has(id)) continue
    seen.add(id)
    if (!out.length || out[out.length - 1].length === size) out.push([])
    out[out.length - 1].push(id)
  }
  return out
}

/** `/api/photos/batch/<subject>/<slot>/<tier>?ids=a,b,c` — the ids are a
 *  query parameter so the path stays cacheable and loggable however many
 *  there are. */
export function batchUrl({ subject = 'note', slot, tier, ids }) {
  const list = ids.map((id) => encodeURIComponent(id)).join(',')
  return `/api/photos/batch/${subject}/${slot}/${tier}?ids=${list}`
}

/**
 * One chunk. Returns `{ok: true, photos}` or `{ok: false, reason, …}`.
 *
 * ⚠ A NON-2xx IS A FAILURE, NOT AN EMPTY ANSWER. 401, 400, 502, 503 all
 * mean *"this request did not answer"*, and the caller must never turn
 * one into "the collector has no photographs".
 */
export async function fetchBatchChunk({ url, token, fetchImpl }) {
  let res
  try {
    res = await fetchImpl(url, { headers: { authorization: `Bearer ${token}` } })
  } catch (err) {
    return { ok: false, reason: FAILURE.REQUEST_FAILED, detail: String(err?.message ?? err) }
  }
  if (!res.ok) {
    return { ok: false, reason: FAILURE.REQUEST_FAILED, status: res.status }
  }
  let body
  try {
    body = await res.json()
  } catch (err) {
    return { ok: false, reason: FAILURE.BAD_RESPONSE, detail: String(err?.message ?? err) }
  }
  /* ⚠ A 200 THAT IS NOT THE CONTRACT IS ALSO A FAILURE. A proxy or an
   * error page answering 200 with something else must not be read as an
   * empty photo list. */
  if (!body || body.ok !== true || !Array.isArray(body.photos)) {
    return { ok: false, reason: FAILURE.BAD_RESPONSE, status: res.status }
  }
  return { ok: true, photos: body.photos }
}

/**
 * Load one public profile's thumbnails.
 *
 * @param username  whose profile is on screen — the cache namespace
 * @param ids       the holding ids the screen is showing, front slot
 * @param tokenImpl `() => Promise<string|null>` — REQUIRED
 *
 * @returns `{photos: Map<id, dataUrl>, unresolved: Set<id>, failed, counts}`
 *
 * ══ ⚠⚠⚠ THE MAP IS SEEDED EMPTY AND FILLED ONLY FROM RETURNED ROWS ══
 *
 * The same shape as `authorizeBatch` on the server, for the same reason,
 * one layer out: **there is no `for (const id of ids)` filling `photos`
 * here.** An id reaches `photos` only by appearing in a response body,
 * and an entry the caller did not ask for is DROPPED rather than
 * rendered — so a confused or hostile response cannot paint a photograph
 * onto a tile this screen never asked about.
 */
export async function loadCommunityThumbs({
  username, ids, subject = 'note', slot, tier,
  fetchImpl = fetch, tokenImpl, cache = _cache,
} = {}) {
  if (!username) throw new Error('loadCommunityThumbs: a username is required')
  if (!slot || !tier) throw new Error('loadCommunityThumbs: slot and tier are required')
  /* ⚠ REQUIRED, NOT DEFAULTED TO null. A defaulted token source would put
   * every caller on the signed-out path forever and look like a profile
   * with no photographs. */
  if (typeof tokenImpl !== 'function') throw new Error('loadCommunityThumbs: tokenImpl is required')

  const wanted = [...new Set((ids ?? []).map((x) => String(x ?? '')).filter(Boolean))]
  const photos = new Map()
  const unresolved = new Set()
  const scope = scopeKey(username, subject, slot, tier)
  const K = (id) => entryKey(username, subject, slot, tier, id)

  /* What the cache already answers, and what is left to ask about. */
  const toAsk = []
  for (const id of wanted) {
    const hit = cache.entries.get(K(id))
    if (hit === undefined) { toAsk.push(id); continue }
    if (hit === UNRESOLVED) unresolved.add(id)
    else photos.set(id, hit)
  }

  const done = (failed) => ({
    photos,
    unresolved,
    failed: failed ?? null,
    counts: { requested: wanted.length, served: photos.size, unresolved: unresolved.size },
  })

  /* ⚠ A REMEMBERED FAILURE SHORT-CIRCUITS, WITHOUT A REQUEST. This is
   * what keeps a dead endpoint from being asked once per keystroke, and
   * `retryCommunityPhotos` is the only thing that lifts it. */
  const remembered = cache.failures.get(scope)
  if (remembered) return done(remembered)
  if (!toAsk.length) return done(null)

  const token = await tokenImpl()
  if (!token) {
    /* ⚠ NOT REMEMBERED AND NOT RETRYABLE. Signing in re-renders, and a
     * Retry button here could never succeed. Nothing is marked
     * unresolved: we did not ask. */
    return done({ reason: FAILURE.SIGNED_OUT, retryable: false, partial: photos.size > 0 })
  }

  let failure = null
  for (const chunk of chunkIds(toAsk)) {
    const r = await fetchBatchChunk({
      url: batchUrl({ subject, slot, tier, ids: chunk }),
      token,
      fetchImpl,
    })

    if (!r.ok) {
      /* ⚠⚠ THE ONE THAT MATTERS. A FAILED CHUNK MARKS NOTHING
       * UNRESOLVED AND CACHES NOTHING. Its ids stay unknown, so the
       * Retry re-asks exactly them — and, far more importantly, the
       * screen never renders a transient fault as the permanent answer.
       * Falling through to `unresolved.add(id)` here is sabotage case 1
       * and it is the defect this whole module is shaped around. */
      failure = failure ?? { reason: r.reason, status: r.status, detail: r.detail, retryable: true }
      continue
    }

    const asked = new Set(chunk)
    for (const p of r.photos) {
      const id = p?.id
      /* ⚠ AN ENTRY WE DID NOT ASK ABOUT IS DROPPED. The response cannot
       * introduce a photograph onto this screen. */
      if (typeof id !== 'string' || !asked.has(id)) continue
      if (typeof p.dataUrl !== 'string' || !p.dataUrl) continue
      photos.set(id, p.dataUrl)
      cache.entries.set(K(id), p.dataUrl)
    }

    /* ⚠ UNRESOLVED IS COMPUTED BY DIFFERENCE AGAINST WHAT ACTUALLY
     * ARRIVED WITH BYTES — not read out of the response's `unresolved`
     * echo. An id the server omitted from BOTH lists would otherwise be
     * silently forgotten instead of rendering as an ordinary refusal. */
    for (const id of chunk) {
      if (photos.has(id)) continue
      unresolved.add(id)
      cache.entries.set(K(id), UNRESOLVED)
    }
  }

  if (failure) {
    failure.partial = photos.size > 0
    cache.failures.set(scope, failure)
  }
  return done(failure)
}
